import { makeAutoObservable, runInAction } from "mobx";
import { validate } from "class-validator";
import type { NavigateFunction } from "react-router-dom";
import {
  superadminDiscountsCreate,
  superadminDiscountsGet,
  superadminDiscountsUpdate,
  superadminListServiceCenters,
} from "@pro-sto/api-client";
import type {
  CreateDiscountDto,
  DiscountRuleDto,
  DiscountResponseDto,
  UpdateDiscountDto,
} from "@pro-sto/api-client";
import { ROUTES } from "@/constants/routes";
import { toastStore } from "./ToastStore";
import { DiscountFormDto } from "./discount-form.dto";
import {
  asOptionalNumber,
  asPlainString,
} from "@/pages/DiscountsPage/discount-format";

export interface DiscountRuleFormItem {
  localId: string;
  rule_type: DiscountRuleDto["rule_type"];
  minSumRubles: string;
  serviceUuid: string;
  maxOrders: string;
  category: string;
}

let ruleIdCounter = 0;
function nextRuleId(): string {
  ruleIdCounter += 1;
  return `rule-${ruleIdCounter}`;
}

function emptyRule(): DiscountRuleFormItem {
  return {
    localId: nextRuleId(),
    rule_type: "min_order_sum",
    minSumRubles: "",
    serviceUuid: "",
    maxOrders: "",
    category: "",
  };
}

function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function dateInputToIso(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const d = new Date(`${trimmed}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

class DiscountFormStore {
  isLoading = false;
  isSubmitting = false;
  submitAttempted = false;
  validationErrors: Record<string, string> = {};
  editUuid: string | null = null;

  title = "";
  description = "";
  type: CreateDiscountDto["type"] = "manual";
  promoCode = "";
  valueType: CreateDiscountDto["value_type"] = "percent";
  valueInput = "10";
  scope: CreateDiscountDto["scope"] = "all_centers";
  audience: CreateDiscountDto["audience"] = "all";
  platformPercent = 50;
  stoPercent = 50;
  startsAt = "";
  endsAt = "";
  isActive = false;
  maxUsesTotal = "";
  maxUsesPerClient = "";
  serviceCenterUuids: string[] = [];
  rules: DiscountRuleFormItem[] = [];

  serviceCenterOptions: Array<{ uuid: string; name: string }> = [];
  isServiceCentersLoading = false;
  private loadRequestId = 0;

  constructor() {
    makeAutoObservable(this);
  }

  resetForCreate = () => {
    this.loadRequestId += 1;
    this.editUuid = null;
    this.title = "";
    this.description = "";
    this.type = "manual";
    this.promoCode = "";
    this.valueType = "percent";
    this.valueInput = "10";
    this.scope = "all_centers";
    this.audience = "all";
    this.platformPercent = 50;
    this.stoPercent = 50;
    this.startsAt = "";
    this.endsAt = "";
    this.isActive = false;
    this.maxUsesTotal = "";
    this.maxUsesPerClient = "";
    this.serviceCenterUuids = [];
    this.rules = [];
    this.submitAttempted = false;
    this.validationErrors = {};
    this.isLoading = false;
    this.isSubmitting = false;
  };

  applyFromResponse = (data: DiscountResponseDto) => {
    this.editUuid = data.uuid;
    this.title = data.title;
    this.description = asPlainString(data.description);
    this.type = data.type;
    this.promoCode = asPlainString(data.promo_code);
    this.valueType = data.value_type;
    this.valueInput =
      data.value_type === "fixed"
        ? String(data.value / 100)
        : String(data.value);
    this.scope = data.scope;
    this.audience = data.audience;
    this.platformPercent = data.platform_percent;
    this.stoPercent = data.sto_percent;
    this.startsAt = toDateInputValue(data.starts_at);
    this.endsAt = toDateInputValue(data.ends_at);
    this.isActive = data.is_active;
    const maxTotal = asOptionalNumber(data.max_uses_total);
    const maxPerClient = asOptionalNumber(data.max_uses_per_client);
    this.maxUsesTotal = maxTotal != null ? String(maxTotal) : "";
    this.maxUsesPerClient = maxPerClient != null ? String(maxPerClient) : "";
    this.serviceCenterUuids = [...(data.service_center_uuids ?? [])];
    this.rules = (data.rules ?? []).map((rule) => {
      const params = rule.params ?? {};
      const item = emptyRule();
      item.localId = nextRuleId();
      item.rule_type = rule.rule_type;
      if (rule.rule_type === "min_order_sum") {
        const minSum = asOptionalNumber(params.min_sum);
        item.minSumRubles =
          minSum != null ? String(minSum / 100) : "";
      }
      if (rule.rule_type === "specific_service") {
        item.serviceUuid = asPlainString(params.service_uuid);
      }
      if (rule.rule_type === "max_orders_count") {
        const maxOrders = asOptionalNumber(params.max_orders);
        item.maxOrders = maxOrders != null ? String(maxOrders) : "";
      }
      if (rule.rule_type === "service_category") {
        item.category = asPlainString(params.category);
      }
      return item;
    });
  };

  loadDiscount = async (uuid: string) => {
    const requestId = ++this.loadRequestId;
    runInAction(() => {
      this.submitAttempted = false;
      this.validationErrors = {};
      this.isLoading = true;
    });
    try {
      const data = await superadminDiscountsGet({ uuid });
      if (requestId !== this.loadRequestId) return;
      runInAction(() => {
        this.applyFromResponse(data);
        this.isLoading = false;
      });
    } catch {
      if (requestId !== this.loadRequestId) return;
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError("Не удалось загрузить скидку");
    }
  };

  loadServiceCenters = async () => {
    if (this.serviceCenterOptions.length > 0) return;
    this.isServiceCentersLoading = true;
    try {
      const response = await superadminListServiceCenters({
        limit: 100,
        page: 1,
      });
      runInAction(() => {
        const raw = response as unknown as {
          data?: Array<{ uuid: string; name: string }>;
        };
        this.serviceCenterOptions = (raw.data ?? [])
          .map((sc) => ({ uuid: sc.uuid, name: sc.name }))
          .sort((a, b) => a.name.localeCompare(b.name, "ru"));
        this.isServiceCentersLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isServiceCentersLoading = false;
      });
    }
  };

  setPlatformPercent = (value: number) => {
    const clamped = Math.min(100, Math.max(0, value));
    this.platformPercent = clamped;
    this.stoPercent = 100 - clamped;
    this.clearFieldError("split");
  };

  setSplitPreset = (platform: number) => {
    this.setPlatformPercent(platform);
  };

  setField = (field: string, value: unknown) => {
    (this as Record<string, unknown>)[field] = value;
    if (this.validationErrors[field]) {
      delete this.validationErrors[field];
    }
  };

  clearFieldError = (field: string) => {
    if (this.validationErrors[field]) {
      delete this.validationErrors[field];
    }
  };

  addRule = () => {
    this.rules = [...this.rules, emptyRule()];
  };

  removeRule = (localId: string) => {
    this.rules = this.rules.filter((r) => r.localId !== localId);
  };

  updateRule = (
    localId: string,
    patch: Partial<DiscountRuleFormItem>,
  ) => {
    this.rules = this.rules.map((r) =>
      r.localId === localId ? { ...r, ...patch } : r,
    );
  };

  toggleServiceCenter = (uuid: string) => {
    if (this.serviceCenterUuids.includes(uuid)) {
      this.serviceCenterUuids = this.serviceCenterUuids.filter((id) => id !== uuid);
    } else {
      this.serviceCenterUuids = [...this.serviceCenterUuids, uuid];
    }
    this.clearFieldError("service_center_uuids");
  };

  private parseValueForApi(): number | null {
    const raw = this.valueInput.replace(",", ".").trim();
    const num = Number(raw);
    if (!Number.isFinite(num) || num <= 0) return null;
    if (this.valueType === "fixed") {
      return Math.round(num * 100);
    }
    if (!Number.isInteger(num) || num > 100) return null;
    return num;
  }

  private buildRulesPayload(): DiscountRuleDto[] {
    return this.rules.map((rule) => {
      const params: Record<string, unknown> = {};
      switch (rule.rule_type) {
        case "min_order_sum": {
          const rub = Number(rule.minSumRubles.replace(",", "."));
          params.min_sum = Math.round(rub * 100);
          break;
        }
        case "specific_service":
          params.service_uuid = rule.serviceUuid.trim();
          break;
        case "first_order":
          break;
        case "max_orders_count": {
          const n = Number(rule.maxOrders);
          params.max_orders = Math.trunc(n);
          break;
        }
        case "service_category":
          params.category = rule.category.trim();
          break;
        default:
          break;
      }
      return { rule_type: rule.rule_type, params };
    });
  }

  private mergeExtraValidationErrors(
    base: Record<string, string>,
  ): Record<string, string> {
    const errors = { ...base };

    if (this.type === "promocode" && !this.promoCode.trim()) {
      errors.promo_code = "Укажите промокод";
    }

    if (
      this.scope === "selected_centers" &&
      this.serviceCenterUuids.length === 0
    ) {
      errors.service_center_uuids = "Выберите хотя бы одно СТО";
    }

    if (this.audience === "selected_clients") {
      errors.audience =
        "Выбор клиентов подключается в задаче STO-227. Пока доступна только аудитория «Все клиенты».";
    }

    const apiValue = this.parseValueForApi();
    if (apiValue == null && !errors.value) {
      errors.value = "Укажите корректный размер скидки";
    }

    return errors;
  }

  submit = async (navigate: NavigateFunction) => {
    this.submitAttempted = true;
    const apiValue = this.parseValueForApi() ?? 0;

    const dto = Object.assign(new DiscountFormDto(), {
      title: this.title.trim(),
      type: this.type,
      value_type: this.valueType,
      value: apiValue,
      platform_percent: this.platformPercent,
      sto_percent: this.stoPercent,
    });

    const classErrors = await validate(dto);
    let validationErrors: Record<string, string> = {};
    for (const error of classErrors) {
      if (error.property === "splitSumMarker") {
        validationErrors.split =
          "Сумма долей платформы и СТО должна быть равна 100%";
      } else if (error.constraints) {
        validationErrors[error.property] = Object.values(error.constraints)[0];
      }
    }
    validationErrors = this.mergeExtraValidationErrors(validationErrors);

    runInAction(() => {
      this.validationErrors = validationErrors;
    });

    if (classErrors.length > 0 || Object.keys(validationErrors).length > 0) {
      return;
    }

    const startsAt = dateInputToIso(this.startsAt);
    const endsAt = dateInputToIso(this.endsAt);

    const maxTotalRaw = this.maxUsesTotal.trim();
    const maxPerClientRaw = this.maxUsesPerClient.trim();
    const maxTotal = maxTotalRaw ? Number(maxTotalRaw) : null;
    const maxPerClient = maxPerClientRaw ? Number(maxPerClientRaw) : null;
    const descriptionTrimmed = this.description.trim();

    const bodyBase: CreateDiscountDto = {
      title: this.title.trim(),
      description: descriptionTrimmed || undefined,
      type: this.type,
      value_type: this.valueType,
      value: apiValue,
      scope: this.scope,
      audience: "all",
      platform_percent: this.platformPercent,
      sto_percent: this.stoPercent,
      starts_at: startsAt ?? undefined,
      ends_at: endsAt ?? undefined,
      is_active: this.isActive,
      max_uses_total:
        maxTotal != null && Number.isFinite(maxTotal) ? maxTotal : undefined,
      max_uses_per_client:
        maxPerClient != null && Number.isFinite(maxPerClient)
          ? maxPerClient
          : undefined,
      service_center_uuids:
        this.scope === "selected_centers" ? this.serviceCenterUuids : [],
      client_uuids: [],
      rules: this.buildRulesPayload(),
    };

    if (this.type === "promocode") {
      bodyBase.promo_code = this.promoCode.trim();
    }

    runInAction(() => {
      this.isSubmitting = true;
    });
    try {
      if (this.editUuid) {
        const updateBody = {
          ...bodyBase,
          description: descriptionTrimmed ? descriptionTrimmed : null,
          starts_at: startsAt,
          ends_at: endsAt,
          max_uses_total:
            maxTotal != null && Number.isFinite(maxTotal) ? maxTotal : null,
          max_uses_per_client:
            maxPerClient != null && Number.isFinite(maxPerClient)
              ? maxPerClient
              : null,
        } as UpdateDiscountDto;
        await superadminDiscountsUpdate({
          uuid: this.editUuid,
          requestBody: updateBody,
        });
        toastStore.showSuccess("Скидка сохранена");
      } else {
        await superadminDiscountsCreate({ requestBody: bodyBase });
        toastStore.showSuccess("Скидка создана");
      }
      runInAction(() => {
        this.isSubmitting = false;
      });
      navigate(ROUTES.DISCOUNTS);
    } catch {
      runInAction(() => {
        this.isSubmitting = false;
      });
      toastStore.showError("Не удалось сохранить скидку");
    }
  };
}

export const discountFormStore = new DiscountFormStore();
export type { DiscountFormStore };
